import {
  Collapse,
  Paper,
  List,
  ClickAwayListener,
  Menu,
  Divider,
  Slider,
  Typography,
  IconButton,
} from "@mui/material";
import {
  Person as PersonIcon,
  Label as LabelIcon,
  Fingerprint as FingerprintIcon,
  BugReport as BugReportIcon,
  FormatListNumbered as FormatListNumberedIcon,
  Percent as PercentIcon,
  TrendingUp as TrendingUpIcon,
  Group as GroupIcon,
  OpenInFull,
  TrendingDown as TrendingDownIcon,
  Expand,
  ZoomOutMap,
  RestartAlt,
} from "@mui/icons-material";
import { useSettings } from "contexts/SettingsManager/SettingsManager";
import { useIsStorybook } from "utils/customHooks/storyHooks";
import { filterObjectByRemovingKeys } from "utils/arrayUtils";
import { useExpandedTileFlags } from "components/Dashboard/Tiles/ProcessTile/processTileUtils";
import type { ExpandedProcessTileConfig } from "types/types";
import { useFlightTranslation } from "utils/customHooks/useFlightTranslation";
import { useIsDarkMode } from "store/atoms";
import { ListItemToggle } from "components/Settings/ListItemToggle";
import { CollapsibleConfigStyles } from "./CollapsibleConfigStyles";
import { useMemo } from "react";
import { css } from "styled-components/macro";
import styled from "styled-components/macro";
import { useIsAdmin } from "utils/customHooks/genericHooks";
import { Tooltip } from "@mui/material";

const SETTING_NAMES_DISPLAY: {
  [isSomethingVisible in keyof ExpandedProcessTileConfig]: string;
} = {
  isTileMetricPercentVisible: "Show_Metric_Percent",
  isTeamSizeVisible: "Show_Team_Size",
  isOperatorNameVisible: "Show_Operator",
  isSkuNameVisible: "Show_SKU_Name",
  isSkuIdVisible: "Show_SKU_ID",
  isTileButterflyVisible: "Show_Butterfly",
  isTileCountVisible: "Show_Count",
  isTileAheadBehindVisible: "Show_Ahead/Behind",
  isTopLossesVisible: "Show_Top_Losses",
};

const ProcessTilesConfigCollapse = ({
  open,
  setIsConfigOpen,
  isSettingsPage = false,
}) => {
  const {
    isLargeTiles,
    setIsLargeTiles,
    tileWidthMultiplier,
    setTileWidthMultiplier,
    tileScaleMultiplier,
    setTileScaleMultiplier,
  } = useSettings();
  const isStorybook = useIsStorybook();
  const { translate } = useFlightTranslation();
  const [isDarkMode] = useIsDarkMode();
  const isAdminUser = useIsAdmin();

  return (
    <Collapse in={open} unmountOnExit={true}>
      <ClickAwayListener onClickAway={() => setIsConfigOpen(false)}>
        <Paper elevation={isSettingsPage ? 0 : 2}>
          <CollapsibleConfigStyles
            $isSettingsPage={isSettingsPage}
            $isDarkMode={isDarkMode}
          >
            <List>
              {/* for now, all tiles configs are boolean on/off switches */}
              {/* isLargeTiles control (on tiles page) */}
              {!isSettingsPage && (
                <ClickableItem
                  onClick={() => setIsLargeTiles(!isLargeTiles)}
                  style={{ position: "relative" }}
                >
                  <ListItemToggle
                    {...(isStorybook ? {} : { button: true })}
                    onChange={() => {}}
                    className={`switchWrapper${isLargeTiles ? " checked" : ""}`}
                    checked={isLargeTiles}
                    label={translate("Large_Tiles")}
                    style={{
                      padding: "0px 8px",
                      height: 42,
                      cursor: "pointer",
                    }}
                    labelStyle={{ padding: "0px 8px" }}
                  />
                </ClickableItem>
              )}
              <ClickableItem
                style={{
                  padding: "8px 8px",
                  display: "flex",
                  flexDirection: "column",
                  gap: "8px",
                  position: "relative",
                  width: "100%",
                  maxWidth: "100%",
                }}
              >
                <div style={{ display: "flex", alignItems: "center" }}>
                  <ZoomOutMap
                    style={{
                      width: ICON_WIDTH,
                      height: ICON_WIDTH,
                      marginRight: "4px",
                      flexShrink: 0,
                    }}
                  />
                  <Typography
                    variant="body2"
                    fontSize="14px"
                    style={{ whiteSpace: "nowrap", flexShrink: 0 }}
                  >
                    {translate("Tile_Scale")}
                  </Typography>
                </div>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    width: "100%",
                    boxSizing: "border-box",
                  }}
                >
                  <Slider
                    size="small"
                    value={tileScaleMultiplier}
                    onChange={(_, value) => {
                      setTileScaleMultiplier(value as number);
                    }}
                    step={0.1}
                    marks
                    min={0.5}
                    max={2}
                    valueLabelDisplay="auto"
                    style={{
                      transition: "opacity 0.2s ease-in-out",
                      marginRight: "4px",
                    }}
                  />
                  <IconButton
                    size="small"
                    onClick={() => setTileScaleMultiplier(1)}
                    sx={{ padding: "2px" }}
                  >
                    <RestartAlt fontSize="small" />
                  </IconButton>
                </div>
              </ClickableItem>
              {isAdminUser && (
                <Tooltip
                  title={
                    "(visible to admins only - once satisfied, apply via flightConfig.general.tileWidthMultiplier)"
                  }
                >
                  <ClickableItem
                    style={{
                      padding: "8px 8px",
                      cursor: "pointer",
                      width: "100%",
                      maxWidth: "100%",
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center" }}>
                      <Expand
                        style={{
                          transform: "rotate(90deg)",
                          width: ICON_WIDTH,
                          height: ICON_WIDTH,
                          marginRight: "4px",
                          flexShrink: 0,
                        }}
                      />
                      <Typography
                        variant="body2"
                        style={{ flexShrink: 0, fontSize: "14px" }}
                      >
                        {translate("Tile_Width")}
                      </Typography>
                    </div>

                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        width: "100%",
                        marginTop: "8px",
                        boxSizing: "border-box",
                      }}
                    >
                      <Slider
                        size="small"
                        value={tileWidthMultiplier}
                        onChange={(_, value) => {
                          setTileWidthMultiplier(value as number);
                        }}
                        step={0.1}
                        marks
                        min={0.5}
                        max={2}
                        valueLabelDisplay="auto"
                        style={{
                          transition: "opacity 0.2s ease-in-out",
                          marginRight: "4px",
                        }}
                      />
                      <IconButton
                        size="small"
                        onClick={() => setTileWidthMultiplier(1)}
                        sx={{ padding: "2px" }}
                      >
                        <RestartAlt fontSize="small" />
                      </IconButton>
                    </div>
                  </ClickableItem>
                </Tooltip>
              )}
              <ExpandedTilesControlsMenuItems />
            </List>
          </CollapsibleConfigStyles>
        </Paper>
      </ClickAwayListener>
    </Collapse>
  );
};

export default ProcessTilesConfigCollapse;
const ICON_WIDTH = 22;
/** mui <Menu> version of ProcessTilesConfig */
export const ProcessTilesConfigMenu = ({
  anchorEl,
  handleClose,
}: {
  anchorEl: HTMLElement | null;
  handleClose: () => void;
}) => {
  const {
    isLargeTiles,
    setIsLargeTiles,
    tileWidthMultiplier,
    setTileWidthMultiplier,
    tileScaleMultiplier,
    setTileScaleMultiplier,
    areProcessTilesExpanded,
    setAreProcessTilesExpanded,
  } = useSettings();
  const isStorybook = useIsStorybook();

  const { translate } = useFlightTranslation();
  const [isDarkMode] = useIsDarkMode();
  const isAdminUser = useIsAdmin();
  return (
    <Menu
      open={Boolean(anchorEl)}
      anchorEl={anchorEl}
      onClose={handleClose}
      elevation={1}
      disableScrollLock={true}
    >
      <CollapsibleConfigStyles
        $isDarkMode={isDarkMode}
        $customCss={css`
          .MuiList-root {
            padding: 0 0 0 8px;
          }
          .switchWrapper {
            padding-right: 0 !important;
          }
        `}
      >
        <List>
          {/* isLargeTiles control (on tiles page) */}
          <ClickableItem
            style={{
              position: "relative",
              cursor: "pointer",
            }}
          >
            <ListItemToggle
              {...(isStorybook ? {} : { button: true })}
              onChange={() => setIsLargeTiles(!isLargeTiles)}
              className={`switchWrapper${isLargeTiles ? " checked" : ""}`}
              checked={isLargeTiles}
              label={translate("Large_Tiles")}
              style={{ padding: "0px 8px", height: 42, fontSize: "16px" }}
              labelStyle={{ padding: "0px 8px", fontSize: "16px" }}
              icon={
                <OpenInFull style={{ width: ICON_WIDTH, height: ICON_WIDTH }} />
              }
            />
          </ClickableItem>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              padding: "8px 8px 8px 8px",
              gap: "8px",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                flexShrink: 0,
              }}
            >
              <ZoomOutMap
                style={{
                  width: ICON_WIDTH,
                  height: ICON_WIDTH,
                  marginRight: "4px",
                  flexShrink: 0,
                }}
              />
              <Typography
                variant="body2"
                fontSize="14px"
                style={{ whiteSpace: "nowrap", flexShrink: 1 }}
              >
                {translate("Tile_Scale")}
              </Typography>
            </div>
            <ClickableItem
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                position: "relative",
                width: "100%",
                maxWidth: "100%",
                boxSizing: "border-box",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  flexGrow: 1,
                  width: 0,
                }}
              >
                <Slider
                  size="small"
                  value={tileScaleMultiplier}
                  onChange={(_, value) => {
                    setTileScaleMultiplier(value as number);
                  }}
                  step={0.1}
                  marks
                  min={0.5}
                  max={2}
                  valueLabelDisplay="auto"
                  style={{
                    transition: "opacity 0.2s ease-in-out",
                    marginRight: "4px",
                  }}
                />
                <IconButton
                  size="small"
                  onClick={() => setTileScaleMultiplier(1)}
                  sx={{ padding: "2px" }}
                >
                  <RestartAlt fontSize="small" />
                </IconButton>
              </div>
            </ClickableItem>
          </div>
          {isAdminUser && (
            <Tooltip
              title={
                "(visible to admins only - once satisfied, apply via flightConfig.general.tileWidthMultiplier)"
              }
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  padding: "8px 8px 8px 8px",
                  gap: "8px",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    flexShrink: 0,
                  }}
                >
                  <Expand
                    style={{
                      transform: "rotate(90deg)",
                      width: ICON_WIDTH,
                      height: ICON_WIDTH,
                      marginRight: "4px",
                      flexShrink: 0,
                    }}
                  />
                  <Typography
                    variant="body2"
                    fontSize="14px"
                    style={{ whiteSpace: "nowrap", flexShrink: 0 }}
                  >
                    {translate("Tile_Width")}
                  </Typography>
                </div>
                <ClickableItem
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    position: "relative",
                    width: "100%",
                    maxWidth: "100%",
                    boxSizing: "border-box",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      flexGrow: 1,
                    }}
                  >
                    <Slider
                      size="small"
                      value={tileWidthMultiplier}
                      onChange={(_, value) => {
                        setTileWidthMultiplier(value as number);
                      }}
                      step={0.1}
                      marks
                      min={0.5}
                      max={2}
                      valueLabelDisplay="auto"
                      style={{
                        transition: "opacity 0.2s ease-in-out",
                        marginRight: "4px",
                      }}
                    />
                    <IconButton
                      size="small"
                      onClick={() => setTileWidthMultiplier(1)}
                      sx={{ padding: "2px" }}
                    >
                      <RestartAlt fontSize="small" />
                    </IconButton>
                  </div>
                </ClickableItem>
              </div>
            </Tooltip>
          )}
          <Divider style={{ margin: "4px 0 4px -8px" }} />
          <ClickableItem
            onClick={() => setAreProcessTilesExpanded(!areProcessTilesExpanded)}
            style={{ position: "relative" }}
          >
            <ListItemToggle
              {...(isStorybook ? {} : { button: true })}
              onChange={() => {}}
              className={`switchWrapper${areProcessTilesExpanded ? " checked" : ""}`}
              checked={areProcessTilesExpanded}
              label={translate("Expand_Tiles")}
              style={{
                padding: "0px 8px",
                height: 42,
                fontSize: "16px",
                cursor: "pointer",
              }}
              labelStyle={{ padding: "0px 8px", fontSize: "16px" }}
              icon={
                <Expand style={{ width: ICON_WIDTH, height: ICON_WIDTH }} />
              }
            />
          </ClickableItem>
          <Divider
            style={{ margin: "4px 0 8px -8px", width: "calc(100% + 16px)" }}
          />
          <ExpandedTilesControlsMenuItems />
        </List>
      </CollapsibleConfigStyles>
    </Menu>
  );
};

function ExpandedTilesControlsMenuItems() {
  const { expandedProcessTileConfig, areProcessTilesExpanded } = useSettings();
  const {
    isOperatorNameAllowed,
    isTileButterflyAllowed,
    isSkuIdAllowed,
    isSkuNameAllowed,
    isTileCountAllowed,
    isAnyTileAheadBehindAllowed,
    isTeamSizeAllowed,
    isTopLossesAllowed,
  } = useExpandedTileFlags(null);

  const { translate } = useFlightTranslation();

  // Define the desired order of settings
  const settingsOrder = [
    "isTileMetricPercentVisible",
    "isTeamSizeVisible",
    "isOperatorNameVisible",
    "isSkuNameVisible",
    "isSkuIdVisible",
    "isTileButterflyVisible",
    "isTileCountVisible",
    "isTileAheadBehindVisible",
    "isTopLossesVisible",
  ];

  const allowedProcessTileConfig = useMemo(() => {
    const keysToRemove = [
      ...(isTeamSizeAllowed ? [] : ["isTeamSizeVisible"]),
      ...(isOperatorNameAllowed ? [] : ["isOperatorNameVisible"]),
      ...(isTileButterflyAllowed ? [] : ["isTileButterflyVisible"]),
      ...(isSkuIdAllowed ? [] : ["isSkuIdVisible"]),
      ...(isSkuNameAllowed ? [] : ["isSkuNameVisible"]),
      ...(isTileCountAllowed ? [] : ["isTileCountVisible"]),
      ...(isAnyTileAheadBehindAllowed ? [] : ["isTileAheadBehindVisible"]),
      ...(isTopLossesAllowed ? [] : ["isTopLossesVisible"]),
    ];

    const allowedProcessTileConfig = filterObjectByRemovingKeys(
      expandedProcessTileConfig,
      keysToRemove
    ) as { settingValue: boolean };

    return allowedProcessTileConfig;
  }, [
    expandedProcessTileConfig,
    isOperatorNameAllowed,
    isTileButterflyAllowed,
    isSkuIdAllowed,
    isSkuNameAllowed,
    isTileCountAllowed,
    isAnyTileAheadBehindAllowed,
    isTeamSizeAllowed,
    isTopLossesAllowed,
  ]);

  return (
    <OptionsContainer $isActive={areProcessTilesExpanded}>
      {
        /* Only show overlay when not hovering over a menu item */
        !areProcessTilesExpanded && (
          <DisabledOverlay
            className="disabled-overlay"
            style={{ pointerEvents: "none" }}
          >
            <Typography variant="caption" color="textSecondary">
              {translate("Enable_Expand_Tiles_To_Use_Options")}
            </Typography>
          </DisabledOverlay>
        )
      }
      <Styles>
        {/* Use the defined order of settings instead of object property order */}
        {settingsOrder
          .filter(settingName => settingName in allowedProcessTileConfig)
          .map(settingName => (
            <ToggleSetting
              key={settingName}
              settingName={settingName}
              settingValue={allowedProcessTileConfig[settingName]}
              icon={ICONS[settingName]}
            />
          ))}
      </Styles>
    </OptionsContainer>
  );
}

const OptionsContainer = styled.div<{ $isActive: boolean }>`
  position: relative;
  margin-top: 8px;
  padding-top: 8px;
  padding-left: 16px;
  border-left: 2px solid ${props => (props.$isActive ? "#1976d2" : "#e0e0e0")};
  opacity: ${props => (props.$isActive ? 1 : 0.7)};
  transition: all 0.2s ease-in-out;
  [class*="ToggleStyles"],
  .MuiFormControlLabel-root {
    cursor: pointer;
  }
`;

const DisabledOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  background: rgba(255, 255, 255, 0.5);
  z-index: 1;
  pointer-events: none;
`;

const ClickableItem = styled.div<{ disabled: boolean }>`
  position: relative;
  transition: all 0.2s ease-in-out;
  cursor: pointer;

  /* Create bright hover state even when disabled */
  &:hover {
    background-color: ${props =>
      props.disabled ? "rgba(25, 118, 210, 0.08)" : "rgba(25, 118, 210, 0.12)"};
    border-radius: 4px;
  }

  /* Add subtle pulsating effect when disabled and hovered */
  &:hover::after {
    content: ${props => (props.disabled ? '""' : "none")};
    position: absolute;
    inset: 0;
    border-radius: 4px;
    border: 1px solid #1976d2;
    opacity: 0.8;
    animation: ${props => (props.disabled ? "pulse 1.5s infinite" : "none")};
    pointer-events: none;
  }

  @keyframes pulse {
    0% {
      opacity: 0.3;
      box-shadow: 0 0 0 0 rgba(25, 118, 210, 0.4);
    }
    70% {
      opacity: 0.8;
      box-shadow: 0 0 0 4px rgba(25, 118, 210, 0);
    }
    100% {
      opacity: 0.3;
      box-shadow: 0 0 0 0 rgba(25, 118, 210, 0);
    }
  }
`;

const Styles = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;
`;

const ICONS = {
  isOperatorNameVisible: <PersonIcon />,
  isSkuNameVisible: <LabelIcon />,
  isSkuIdVisible: <FingerprintIcon />,
  isTileButterflyVisible: <BugReportIcon />,
  isTileCountVisible: <FormatListNumberedIcon />,
  isTileMetricPercentVisible: <PercentIcon />,
  isTileAheadBehindVisible: <TrendingUpIcon />,
  isTeamSizeVisible: <GroupIcon />,
  isTopLossesVisible: <TrendingDownIcon />,
};

function ToggleSetting({
  settingName,
  settingValue,
  icon = null,
}: {
  settingName: string;
  settingValue: boolean;
  icon?: JSX.Element | null;
}) {
  const {
    expandedProcessTileConfig,
    setExpandedProcessTileConfig,
    areProcessTilesExpanded,
    setAreProcessTilesExpanded,
  } = useSettings();

  const handleChange = () => {
    setExpandedProcessTileConfig({
      ...expandedProcessTileConfig,
      [settingName]: !settingValue,
    });
  };
  const { translate } = useFlightTranslation();

  const disabled = !areProcessTilesExpanded;

  // Check if this is the "Show Count" setting and if "Show Ahead/Behind" is enabled
  const isCountSetting = settingName === "isTileCountVisible";
  const isAheadBehindEnabled =
    expandedProcessTileConfig?.isTileAheadBehindVisible;
  const shouldShowTooltip = isCountSetting && isAheadBehindEnabled;

  // Create style for the greyed out appearance when count should be greyed out
  const greyedOutStyle = shouldShowTooltip
    ? {
        opacity: 0.6,
        color: "rgba(0, 0, 0, 0.6)",
      }
    : {};

  const toggleComponent = (
    <ListItemToggle
      disabled={disabled}
      className={`switchWrapper${settingValue ? " checked" : ""}`}
      checked={settingValue}
      onChange={() => {
        handleChange();
        /* Handle click through parent element instead */
      }}
      label={translate(SETTING_NAMES_DISPLAY[settingName])}
      style={{
        padding: "0px 8px",
        height: 42,
        opacity: 1, // Always show at full opacity
        cursor: "pointer", // Always show pointer cursor
        transition: "all 0.2s ease-in-out",
        ...greyedOutStyle, // Apply greyed out style if needed
      }}
      labelStyle={{ padding: "0px 8px" }}
      icon={icon}
    />
  );

  return (
    <ClickableItem
      disabled={disabled}
      onClick={() => {
        // if it's disabled it but we're trying to use it, enable it
        if (disabled) {
          setAreProcessTilesExpanded(true);
        }
      }}
    >
      {shouldShowTooltip ? (
        <Tooltip title="Ahead/Behind includes Count" placement="right">
          {toggleComponent}
        </Tooltip>
      ) : (
        toggleComponent
      )}
    </ClickableItem>
  );
}
